import { formatCurrencyID, formatQuantity } from '../../utils/currency';
import { formatRupiah } from '../../utils/printHelper';
import { SearchableSelect } from '../SearchableSelect';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import { CalendarIcon, Plus, Trash2 } from 'lucide-react';
import { Calendar } from '../ui/calendar';

export function InvoiceForm({ formAmounts, companies, formData, handleAddAddress, handleAddItem, handleAddressChange, handleCreate, handleEdit, handleItemChange, handleRemoveAddress, handleRemoveItem, handleSupplierChange, invoiceDate, isEdit, selectedInvoice, setFormData, setInvoiceDate, suppliers }) {
  const { subtotal, ppnAmount, pphAmount, adminBank, totalTagihan, nilaiDibayarkan } = formAmounts;
  return (
    <form onSubmit={isEdit ? handleEdit : handleCreate} className="space-y-4">
      {/* Invoice Number & Date */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Nomor Invoice *</Label>
          {isEdit ? (
            <div className="flex items-center gap-2 px-3 py-2 border rounded-md bg-slate-50">
              <span className="text-sm font-mono">{selectedInvoice?.invoice_number}</span>
            </div>
          ) : (
            <Input
              value={formData.invoice_number}
              onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value.toUpperCase() })}
              placeholder="MASUKKAN NOMOR INVOICE"
              style={{ textTransform: 'uppercase' }}
              className="font-mono"
              required
            />
          )}
          <p className="text-xs text-slate-500">{isEdit ? 'Nomor Invoice tidak dapat diubah' : 'Masukkan nomor invoice secara manual'}</p>
        </div>
        <div className="space-y-2">
          <Label>Tanggal Invoice *</Label>
          <Popover modal={false}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal"
                data-testid="invoice-date-picker"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {invoiceDate ? format(invoiceDate, 'dd MMMM yyyy', { locale: id }) : 'Pilih tanggal invoice'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 z-[100]" align="start">
              <Calendar
                mode="single"
                selected={invoiceDate}
                onSelect={setInvoiceDate}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <p className="text-xs text-slate-500">Pilih tanggal invoice secara manual</p>
        </div>
      </div>

      {/* Supplier Selection */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Supplier *</Label>
          <SearchableSelect
            options={suppliers}
            value={formData.supplier_id}
            onChange={handleSupplierChange}
            displayKey="nama_supplier"
            valueKey="id"
            placeholder="Ketik nama supplier..."
            testId="supplier-select"
          />
        </div>
        <div className="space-y-2">
          <Label>Email Supplier</Label>
          <Input
            type="email"
            value={formData.email_supplier}
            onChange={(e) => setFormData({ ...formData, email_supplier: e.target.value.toLowerCase() })}
            placeholder="email@supplier.com"
            style={{ textTransform: 'lowercase' }}
          />
        </div>
      </div>

      {/* WhatsApp Number */}
      <div className="space-y-2">
        <Label>Nomor WhatsApp Supplier</Label>
        <Input
          type="tel"
          value={formData.nomor_wa_supplier}
          onChange={(e) => setFormData({ ...formData, nomor_wa_supplier: e.target.value.replace(/[^0-9]/g, '') })}
          placeholder="6281234567890"
        />
      </div>

      {/* Company Selection */}
      <div className="space-y-2">
        <Label>Ditujukan untuk (Perusahaan) *</Label>
        <SearchableSelect
          options={companies}
          value={formData.company_id}
          onChange={(value) => setFormData({ ...formData, company_id: value })}
          displayKey="nama_perusahaan"
          valueKey="id"
          placeholder="Ketik nama perusahaan..."
          testId="company-select"
        />
      </div>

      {/* Alamat Pengiriman */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Alamat Pengiriman *</Label>
          <Button type="button" size="sm" variant="outline" onClick={handleAddAddress}>
            <Plus className="h-4 w-4 mr-1" /> Tambah Alamat
          </Button>
        </div>
        {formData.alamat_pengiriman.map((address, index) => (
          <div key={index} className="flex gap-2">
            <textarea
              className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm uppercase"
              value={address}
              onChange={(e) => handleAddressChange(index, e.target.value)}
              placeholder={`ALAMAT PENGIRIMAN ${index + 1}`}
              required={index === 0}
            />
            {formData.alamat_pengiriman.length > 1 && (
              <Button type="button" variant="destructive" size="sm" onClick={() => handleRemoveAddress(index)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
      </div>

      {/* Termin & Jenis Pengiriman */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Termin Pembayaran *</Label>
          <Select 
            value={formData.termin_pembayaran.toString()} 
            onValueChange={(value) => setFormData({ ...formData, termin_pembayaran: parseInt(value) })}
          >
            <SelectTrigger data-testid="termin-select">
              <SelectValue placeholder="Pilih Termin" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 Hari</SelectItem>
              <SelectItem value="14">14 Hari</SelectItem>
              <SelectItem value="20">20 Hari</SelectItem>
              <SelectItem value="30">30 Hari</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Jenis Pengiriman *</Label>
          <Select 
            value={formData.jenis_pengiriman} 
            onValueChange={(value) => setFormData({ ...formData, jenis_pengiriman: value })}
          >
            <SelectTrigger data-testid="delivery-type-select">
              <SelectValue placeholder="Pilih Jenis Pengiriman" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="full">Full (Sekaligus)</SelectItem>
              <SelectItem value="partial">Partial (Bertahap)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Items */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>Items *</Label>
          <Button type="button" size="sm" onClick={handleAddItem} data-testid="add-item-button">
            <Plus className="h-4 w-4 mr-1" /> Tambah Item
          </Button>
        </div>
        <div className="border rounded-lg p-3 space-y-3">
          {formData.items.map((item, index) => (
            <div key={index} className="space-y-2 pb-3 border-b last:border-b-0 last:pb-0">
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-5">
                  <Label className="text-xs">Nama Item</Label>
                  <Input
                    value={item.name}
                    onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                    placeholder="NAMA ITEM"
                    style={{ textTransform: 'uppercase' }}
                    required
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Satuan</Label>
                  <Select 
                    value={item.satuan || 'pcs'} 
                    onValueChange={(value) => handleItemChange(index, 'satuan', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Satuan" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pcs">Pcs</SelectItem>
                      <SelectItem value="unit">Unit</SelectItem>
                      <SelectItem value="box">Box</SelectItem>
                      <SelectItem value="pack">Pack</SelectItem>
                      <SelectItem value="set">Set</SelectItem>
                      <SelectItem value="lusin">Lusin</SelectItem>
                      <SelectItem value="kg">Kg</SelectItem>
                      <SelectItem value="gram">Gram</SelectItem>
                      <SelectItem value="liter">Liter</SelectItem>
                      <SelectItem value="meter">Meter</SelectItem>
                      <SelectItem value="roll">Roll</SelectItem>
                      <SelectItem value="zak">Zak</SelectItem>
                      <SelectItem value="ekor">Ekor</SelectItem>
                      <SelectItem value="karton">Karton</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Qty</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={item.quantity_display ?? formatQuantity(item.quantity)}
                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                    required
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Harga (Rp)</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={item.unit_price_display ?? formatCurrencyID(item.unit_price)}
                    onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                    placeholder="0"
                    required
                  />
                </div>
                <div className="col-span-1">
                  {formData.items.length > 1 && (
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => handleRemoveItem(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
              {formData.jenis_pengiriman === 'partial' && (
                <div className="flex items-center gap-2">
                  <Label className="text-xs whitespace-nowrap">Tanggal Kirim:</Label>
                  <Popover modal={false}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-[200px] justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {item.delivery_date ? format(new Date(item.delivery_date), 'dd MMM yyyy', { locale: id }) : 'Pilih tanggal'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 z-[100]" align="start">
                      <Calendar
                        mode="single"
                        selected={item.delivery_date ? new Date(item.delivery_date) : undefined}
                        onSelect={(date) => handleItemChange(index, 'delivery_date', date ? format(date, 'yyyy-MM-dd') : '')}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* PPn Option */}
      <div className="space-y-3 border rounded-lg p-4 bg-slate-50">
        <div className="flex items-center space-x-2">
          <Checkbox 
            id={isEdit ? "edit_has_ppn" : "has_ppn"}
            checked={formData.has_ppn}
            onCheckedChange={(checked) => setFormData({ ...formData, has_ppn: checked })}
            data-testid="ppn-checkbox"
          />
          <Label htmlFor={isEdit ? "edit_has_ppn" : "has_ppn"} className="cursor-pointer">Tambahkan PPn (Pajak Pertambahan Nilai)</Label>
        </div>
        {formData.has_ppn && (
          <div className="space-y-2 pl-6">
            <Label htmlFor={isEdit ? "edit_ppn_percentage" : "ppn_percentage"}>Persentase PPn (%)</Label>
            <Input
              id={isEdit ? "edit_ppn_percentage" : "ppn_percentage"}
              type="number"
              value={formData.ppn_percentage}
              onChange={(e) => setFormData({ ...formData, ppn_percentage: parseFloat(e.target.value) || 0 })}
              min="0"
              max="100"
              step="0.1"
              className="w-32"
              data-testid="ppn-percentage-input"
            />
          </div>
        )}
      </div>

      {/* PPh Section */}
      <div className="space-y-2 border rounded-lg p-4 bg-rose-50">
        <div className="flex items-center gap-2">
          <Checkbox
            id={isEdit ? "edit_has_pph" : "has_pph"}
            checked={formData.pph_type !== '' && formData.pph_type !== null}
            onCheckedChange={(checked) => setFormData({ ...formData, pph_type: checked ? 'PPh23' : '', pph_percentage: checked ? 2 : 0 })}
          />
          <Label htmlFor={isEdit ? "edit_has_pph" : "has_pph"} className="cursor-pointer text-rose-700 font-medium">Potongan PPh (Pajak Penghasilan)</Label>
        </div>
        {formData.pph_type && (
          <div className="space-y-3 pl-6">
            <div className="space-y-2">
              <Label htmlFor={isEdit ? "edit_pph_type" : "pph_type"}>Jenis PPh</Label>
              <Select
                value={formData.pph_type}
                onValueChange={(value) => {
                  let defaultPercentage = 0;
                  if (value === 'PPh21') defaultPercentage = 5;
                  else if (value === 'PPh23') defaultPercentage = 2;
                  else if (value === 'PPh4(2)') defaultPercentage = 10;
                  setFormData({ ...formData, pph_type: value, pph_percentage: defaultPercentage });
                }}
              >
                <SelectTrigger className="w-48" data-testid="pph-type-select">
                  <SelectValue placeholder="Pilih jenis PPh" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PPh21">PPh 21 (Tenaga Kerja)</SelectItem>
                  <SelectItem value="PPh23">PPh 23 (Jasa/Sewa)</SelectItem>
                  <SelectItem value="PPh4(2)">PPh 4 ayat 2 (Final)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor={isEdit ? "edit_pph_percentage" : "pph_percentage"}>Persentase PPh (%)</Label>
              <Input
                id={isEdit ? "edit_pph_percentage" : "pph_percentage"}
                type="number"
                value={formData.pph_percentage}
                onChange={(e) => setFormData({ ...formData, pph_percentage: parseFloat(e.target.value) || 0 })}
                min="0"
                max="100"
                step="0.1"
                className="w-32"
                data-testid="pph-percentage-input"
              />
              <p className="text-xs text-rose-600">* PPh dihitung dari nilai sebelum PPN (Subtotal)</p>
            </div>
          </div>
        )}
      </div>

      {/* Admin Bank Section */}
      <div className="space-y-2 border rounded-lg p-4 bg-blue-50">
        <Label htmlFor={isEdit ? "edit_admin_bank" : "admin_bank"} className="text-blue-700 font-medium">Admin Bank (Rp)</Label>
        <Input
          id={isEdit ? "edit_admin_bank" : "admin_bank"}
          type="number"
          value={formData.admin_bank}
          onChange={(e) => setFormData({ ...formData, admin_bank: parseFloat(e.target.value) || 0 })}
          min="0"
          step="1000"
          className="w-48"
          placeholder="0"
          data-testid="admin-bank-input"
        />
        <p className="text-xs text-blue-600">* Biaya admin bank yang dipotong dari pembayaran</p>
      </div>

      {/* Calculation Summary */}
      <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg space-y-2">
        <div className="flex justify-between text-sm">
          <span>Subtotal (DPP)</span>
          <span className="font-mono">Rp {formatRupiah(subtotal)}</span>
        </div>
        {formData.has_ppn && (
          <div className="flex justify-between text-sm">
            <span>PPn ({formData.ppn_percentage}%)</span>
            <span className="font-mono">Rp {formatRupiah(ppnAmount)}</span>
          </div>
        )}
        <div className="flex justify-between font-bold text-lg border-t pt-2">
          <span>Total Tagihan</span>
          <span className="font-mono text-orange-600">Rp {formatRupiah(totalTagihan)}</span>
        </div>
        {pphAmount > 0 && (
          <div className="flex justify-between text-sm text-rose-600">
            <span>PPh ({formData.pph_type} {formData.pph_percentage}%) - dari DPP</span>
            <span className="font-mono">- Rp {formatRupiah(pphAmount)}</span>
          </div>
        )}
        {adminBank > 0 && (
          <div className="flex justify-between text-sm text-blue-600">
            <span>Admin Bank</span>
            <span className="font-mono">- Rp {formatRupiah(adminBank)}</span>
          </div>
        )}
        <div className="flex justify-between font-bold text-lg border-t pt-2">
          <span>Nilai Dibayarkan</span>
          <span className="font-mono text-emerald-700">Rp {formatRupiah(nilaiDibayarkan)}</span>
        </div>
      </div>

      {/* Notes */}
      <div className="space-y-2">
        <Label htmlFor={isEdit ? "edit_notes" : "notes"}>Catatan (Optional)</Label>
        <textarea
          id={isEdit ? "edit_notes" : "notes"}
          className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm uppercase"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value.toUpperCase() })}
          placeholder="TAMBAHKAN CATATAN JIKA DIPERLUKAN..."
        />
      </div>

      <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-700" data-testid={isEdit ? "update-invoice-button" : "submit-invoice-button"}>
        {isEdit ? 'Update Invoice' : 'Buat Invoice'}
      </Button>
    </form>
  );
}
